import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ScrollToTop from './components/ScrollToTop';

// Layouts
import PublicLayout from './components/PublicLayout';
import AdminLayout from './components/AdminLayout';

// Public Pages
import HomePage from './pages/HomePage';
import OffersPage from './pages/OffersPage';
import TouristPlanPage from './pages/TouristPlanPage';
import InsurancePage from './pages/InsurancePage';
import PassportPage from './pages/PassportPage';
import ConciergeriePage from './pages/ConciergeriePage';
import VisaPage from './pages/VisaPage';
import VisaDetailPage from './pages/VisaDetailPage';
import VisaOtherPage from './pages/VisaOtherPage';
import DubaiVisaWorkflowPage from './pages/DubaiVisaWorkflowPage';
import VisaDownloadPage from './pages/VisaDownloadPage';
import VisaReceiptPage from './pages/VisaReceiptPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import ProfileFormPage from './pages/ProfileFormPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import VisaApplicationsPage from './pages/admin/VisaApplicationsPage';
import OffersManagementPage from './pages/admin/OffersManagementPage';
import ServiceRequestsPage from './pages/admin/ServiceRequestsPage';
import UsersManagementPage from './pages/admin/UsersManagementPage';
import AppointmentsPage from './pages/admin/AppointmentsPage';
import ProspectsPage from './pages/admin/ProspectsPage';
import SiteServicesManagementPage from './pages/admin/SiteServicesManagementPage';
import VisaPricesPage from './pages/admin/VisaPricesPage';

// Agent Pages
import AgentDashboard from './pages/agent/AgentDashboard';

// Reception Pages
import ReceptionDashboard from './pages/reception/ReceptionDashboard';

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, isStaff, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!user || !isStaff()) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Public Route with Layout
const PublicRoute = ({ children }) => {
  return <PublicLayout>{children}</PublicLayout>;
};

// Admin Route with Layout
const AdminRoute = ({ children, requireAdmin = false }) => {
  return (
    <ProtectedRoute requireAdmin={requireAdmin}>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><HomePage /></PublicRoute>} />
      <Route path="/offres" element={<PublicRoute><OffersPage /></PublicRoute>} />
      <Route path="/services/plan-touristique" element={<PublicRoute><TouristPlanPage /></PublicRoute>} />
      <Route path="/services/assurance" element={<PublicRoute><InsurancePage /></PublicRoute>} />
      <Route path="/services/passeport" element={<PublicRoute><PassportPage /></PublicRoute>} />
      <Route path="/conciergerie" element={<PublicRoute><ConciergeriePage /></PublicRoute>} />
      <Route path="/visa" element={<PublicRoute><VisaPage /></PublicRoute>} />
      <Route path="/visa/autres" element={<PublicRoute><VisaOtherPage /></PublicRoute>} />
      <Route path="/visa/dubai/apply" element={<PublicRoute><DubaiVisaWorkflowPage /></PublicRoute>} />
      <Route path="/visa/dubai/download/:applicationId" element={<VisaDownloadPage />} />
      <Route path="/visa/receipt" element={<VisaReceiptPage />} />
      <Route path="/visa/:destinationId" element={<PublicRoute><VisaDetailPage /></PublicRoute>} />
      <Route path="/contact" element={<PublicRoute><ContactPage /></PublicRoute>} />
      <Route path="/login" element={<LoginPage />} />
      
      {/* Profile Form (Public with token) */}
      <Route path="/profil/:token" element={<ProfileFormPage />} />
      
      {/* Password Reset Routes */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/visa-applications" element={<AdminRoute><VisaApplicationsPage /></AdminRoute>} />
      <Route path="/admin/offers" element={<AdminRoute><OffersManagementPage /></AdminRoute>} />
      <Route path="/admin/services" element={<AdminRoute requireAdmin><SiteServicesManagementPage /></AdminRoute>} />
      <Route path="/admin/visa-prices" element={<AdminRoute requireAdmin><VisaPricesPage /></AdminRoute>} />
      <Route path="/admin/service-requests" element={<AdminRoute><ServiceRequestsPage /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute requireAdmin><UsersManagementPage /></AdminRoute>} />
      <Route path="/admin/appointments" element={<AdminRoute><AppointmentsPage /></AdminRoute>} />
      <Route path="/admin/prospects" element={<AdminRoute><ProspectsPage /></AdminRoute>} />

      {/* Agent Routes */}
      <Route path="/agent" element={<ProtectedRoute><AgentDashboard /></ProtectedRoute>} />

      {/* Reception Routes */}
      <Route path="/reception" element={<ProtectedRoute><ReceptionDashboard /></ProtectedRoute>} />
      <Route path="/reception/dashboard" element={<ProtectedRoute><ReceptionDashboard /></ProtectedRoute>} />

      {/* Payment Routes */}
      <Route path="/payment/success" element={<PublicRoute><PaymentSuccessPage /></PublicRoute>} />
      <Route path="/payment/cancel" element={<PublicRoute><PaymentCancelPage /></PublicRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Simple payment result pages
const PaymentSuccessPage = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const sessionId = searchParams.get('session_id');
  
  React.useEffect(() => {
    if (sessionId) {
      // Poll payment status
      const checkStatus = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
          const data = await response.json();
          console.log('Payment status:', data);
        } catch (error) {
          console.error('Error checking payment:', error);
        }
      };
      checkStatus();
    }
  }, [sessionId]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-[#0F172A] mb-4">Paiement Réussi!</h1>
        <p className="text-gray-600 mb-8">Merci pour votre paiement. Vous recevrez un email de confirmation.</p>
        <a href="/" className="inline-block bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8 py-3">
          Retour à l'accueil
        </a>
      </div>
    </div>
  );
};

const PaymentCancelPage = () => {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-[#0F172A] mb-4">Paiement Annulé</h1>
        <p className="text-gray-600 mb-8">Votre paiement a été annulé. Aucun montant n'a été débité.</p>
        <a href="/" className="inline-block bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8 py-3">
          Retour à l'accueil
        </a>
      </div>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
